import { Stack } from "@mui/material";
import React, { useEffect } from "react";
import { Controller } from "react-hook-form";

import { useDetectorGroups } from "api/services/useDetectorGroups";
import { Modal } from "new-components/modals/Modal";
import { TextFieldInput } from "new-components/textFieldInput/TextFieldInput";
import { Typography } from "new-components/typography/Typography";

import { useDetectorGroupForm } from "./form";

export type ModalAction<Data> =
  | {
      action: "create";
    }
  | {
      action: "update";
      data: Data;
    };

export type DetectorGroupModalData = {
  id: string;
  name: string;
};

type DetectorGroupFormModalProps = {
  action: ModalAction<DetectorGroupModalData> | null;
  onClose: () => void;
};

export function DetectorGroupFormModal({ action, onClose }: DetectorGroupFormModalProps) {
  const { control, reset, formState, handleSubmit } = useDetectorGroupForm();

  useEffect(() => {
    if (action?.action === "update") {
      reset({
        name: action.data.name,
      });
    } else {
      reset({ name: "" });
    }
  }, [action, reset]);

  const {
    // create operations
    createDetectorGroup,
    isCreatingDetectorGroup,

    // update operations
    updateDetectorGroup,
    isUpdatingDetectorGroup,
  } = useDetectorGroups();

  const submit = handleSubmit((data) => {
    if (action?.action === "update") {
      updateDetectorGroup(
        {
          id: action.data.id,
          name: data.name,
        },
        {
          onSuccess: () => {
            onClose();
            reset();
          },
        }
      );
      return;
    }

    createDetectorGroup(
      {
        name: data.name,
      },
      {
        onSuccess: () => {
          onClose();
          reset();
        },
      }
    );
  });

  return (
    <Modal
      title={action?.action === "update" ? "Update group" : "Create new group"}
      open={action !== null}
      size="small"
      titleAlignment="left"
      onClose={() => {
        onClose();
        reset();
      }}
      onCancel={() => {
        onClose();
        reset();
      }}
      confirmationButtonTitle="Save"
      isConfirmationDisabled={
        !formState.isValid || formState.isValidating || isCreatingDetectorGroup || isUpdatingDetectorGroup
      }
      isConfirmationLoading={isCreatingDetectorGroup || isUpdatingDetectorGroup}
      onConfirmation={() => {
        submit();
      }}
      body={
        <Controller
          control={control}
          name="name"
          render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
            <Stack width="100%" height="100%" py={2} gap="10px">
              <Typography>Group name:</Typography>

              <TextFieldInput
                variant="standard"
                value={value}
                onChange={onChange}
                error={invalid}
                helperText={error?.message}
              />
            </Stack>
          )}
        />
      }
    />
  );
}
